import React from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete, TextField, Chip, Box, FormControl,
} from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ChevronDown, X } from 'lucide-react';

// Redesigned (design-feedback) styles — bordered outlined input + brand chips.
// Opt-in via the `redesign` prop so legacy consumers keep the standard look.
const redesignInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: 14,
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#e0e1e6' },
    '&:hover fieldset': { borderColor: '#3d5a8a' },
    '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(61, 90, 138, 0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#3d5a8a', borderWidth: '1px' },
  },
  '& .MuiInputBase-input::placeholder': { color: '#546077', opacity: 1 },
  '& .MuiAutocomplete-popupIndicator': { color: '#546077' },
};

// Chips mirror the prototype: pl-2 pr-1 py-0.5 bg-[#e8eef7] text-brand-700 rounded text-xs
const redesignChipSx = {
  backgroundColor: '#e8eef7',
  color: '#213d69',
  fontWeight: 500,
  fontSize: 12,
  borderRadius: '4px',
  height: 22,
  '& .MuiChip-label': { paddingLeft: '8px', paddingRight: '4px' },
  '& .MuiChip-deleteIcon': {
    color: '#213d69',
    margin: '0 4px 0 0',
    borderRadius: '2px',
    '&:hover': { color: '#213d69', backgroundColor: 'rgba(61, 90, 138, 0.1)' },
  },
};

// Dropdown panel mirrors the prototype: mt-1 border-rule rounded-[8px] shadow-lg
// max-h-60 py-1; options px-3 py-2 text-sm (selected = ink-500, hover = surface-page)
const redesignPaperSx = {
  marginTop: '4px',
  border: '1px solid #e0e1e6',
  borderRadius: '8px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  '& .MuiAutocomplete-listbox': {
    maxHeight: 240,
    padding: '4px 0',
    '& .MuiAutocomplete-option': {
      padding: '8px 12px',
      fontSize: 14,
      fontWeight: 500,
      color: '#0e1e40',
      '&.Mui-focused': { backgroundColor: '#fbfbfc' },
      '&[aria-selected="true"]': { color: '#546077', backgroundColor: 'transparent' },
      '&[aria-selected="true"].Mui-focused': { backgroundColor: '#fbfbfc' },
    },
  },
};

export function ChipSelectSearch({
  label,
  members,
  handleChange,
  selectedMembers,
  className,
  id,
  handleSelectAll,
  placeholder = 'required',
  selectAllBtn = false,
  redesign = false,
  count = null,
  ...props
}) {
  const total = members?.length || 0;
  const allSelected = total > 0 && (selectedMembers?.length || 0) >= total;
  const unselectAll = () => handleChange({ target: { name: id, value: [] } });
  const onSelectAllToggle = allSelected ? unselectAll : handleSelectAll;
  const selectAllLabel = allSelected ? 'Unselect All' : 'Select All';

  if (redesign) {
    return (
      <FormControl variant="standard" fullWidth>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium text-ink-900">{label}</span>
          <span className="flex items-center gap-2">
            {count && (
              <span className="text-xs text-ink-500 tabular-nums">{count}</span>
            )}
            {selectAllBtn && (
              <button
                type="button"
                onClick={onSelectAllToggle}
                className={'text-sm font-medium text-[#53A551] hover:text-[#458a44] '
                  + 'transition bg-transparent border-0 p-0'}
              >
                {selectAllLabel}
              </button>
            )}
          </span>
        </div>
        <Autocomplete
          multiple
          id={id}
          size="small"
          options={members}
          value={selectedMembers}
          onChange={(event, newValue) => {
            handleChange({ target: { name: id, value: newValue } });
          }}
          getOptionLabel={(option) => option.name}
          popupIcon={<ChevronDown size={14} />}
          componentsProps={{ paper: { sx: redesignPaperSx } }}
          renderInput={(params) => (
            <TextField
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...params}
              placeholder={selectedMembers?.length ? '' : placeholder}
              variant="outlined"
              sx={redesignInputSx}
              inputProps={{
                ...params.inputProps,
                'data-testid': 'timezone',
              }}
            />
          )}
          renderTags={(value, getTagProps) => value.map((option, index) => (
            <Chip
              key={index}
              label={option.name}
              size="small"
              sx={redesignChipSx}
              deleteIcon={<X size={11} />}
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...getTagProps({ index })}
              onDelete={() => {
                const filtered = selectedMembers.filter((item) => item !== option);
                handleChange({ target: { name: id, value: filtered } });
              }}
            />
          ))}
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          {...props}
        />
      </FormControl>
    );
  }

  return (
    <FormControl sx={{ marginBottom: '16px' }} variant="standard" fullWidth>
      {/* Select All Button */}
      {selectAllBtn && (
        <Box
          sx={{
            color: 'var(--green-3, #53A551)',
            textAlign: 'right',
            fontFamily: 'Inter',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px',
            position: 'absolute',
            right: 0,
            top: 0,
            cursor: 'pointer',
            zIndex: 2,
          }}
          onClick={onSelectAllToggle}
        >
          {selectAllLabel}
        </Box>
      )}

      {/* Autocomplete component for searchable selection */}
      <Autocomplete
        multiple
        id={id}
        options={members}
        value={selectedMembers}
        onChange={(event, newValue) => {
          handleChange({ target: { name: id, value: newValue } });
        }}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...params}
            label={placeholder}
            variant="standard"
            inputProps={{
              ...params.inputProps,
              'data-testid': 'timezone',
            }}
          />
        )}
        renderTags={(value, getTagProps) => value.map((option, index) => (
          <Chip
            key={index}
            label={option.name}
            size="small"
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...getTagProps({ index })}
            onDelete={() => {
              const filtered = selectedMembers.filter((item) => item !== option);
              handleChange({ target: { name: id, value: filtered } });
            }}
          />
        ))}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
      />
    </FormControl>
  );
}

ChipSelectSearch.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  selectedMembers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  placeholder: PropTypes.string,
  selectAllBtn: PropTypes.bool,
  handleSelectAll: PropTypes.func,
  redesign: PropTypes.bool,
  count: PropTypes.string,
};
